import classNames from 'classnames';
import Icon from 'components/icon/Icon';
import { Portal } from 'components/Portal/Portal';
import './Tooltip.scss';
import { useTogglerContext } from 'contexts/TogglerContext';
import {
  useClick,
  useDismiss,
  useInteractions,
  useFloating,
  autoUpdate,
  offset,
  flip
} from '@floating-ui/react';

const ToggletipPopper = props => {
  const { isOpen, setOpen } = useTogglerContext();

  const {
    id,
    children,
    extraClassNames,
    icon,
    info,
    mediumIcon,
    alert,
    video,
    videoMessage,
    label,
    stylesLabel,
    iconStrokeColor,
    tabIndex
  } = props;

  const open = isOpen === id;

  const { refs, floatingStyles, context } = useFloating({
    placement: 'top-end',
    strategy: 'absolute',
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip()]
  });
  const click = useClick(context);
  const dismiss = useDismiss(context, {
    outsidePressEvent: 'mousedown'
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss
  ]);

  return (
    <div
      className={classNames('c-tooltip c-tooltip--popper', extraClassNames, {
        'c-tooltip--alert': alert,
        'c-tooltip--info': info,
        'c-tooltip--video': video,
        'c-tooltip--label': label?.length > 0
      })}
    >
      <button
        className={classNames('c-button c-button--tiny c-tooltip__trigger', {
          'c-button--disabled': props.disabled || props.pseudoDisabled, // ok
          'c-tooltip__trigger--active': open
        })}
        type={'button'}
        aria-labelledby={id?.length && !video ? id : `toggletip-label-${id}`}
        onClick={() => {
          open ? setOpen(false) : setOpen(id);
        }}
        ref={refs.setReference}
        tabIndex={tabIndex}
        {...getReferenceProps()}
      >
        {icon &&
          (!open ? (
            <Icon
              id={icon}
              mediumIcon={mediumIcon}
              strokeColor={
                iconStrokeColor
                  ? iconStrokeColor
                  : alert
                    ? 'color-error'
                    : false
              }
            />
          ) : (
            <Icon
              id={'close'}
              strokeColor={'color-neutral-dark'}
              style={{
                borderRadius: 16,
                padding: 2,
                strokeWidth: 2,
                border: '1px solid gray'
              }}
            />
          ))}
        {/* TODO: accessible info to toggletip when toggletip is not open? */}
        {label && (
          <span id={`toggletip-label-${id}`} style={stylesLabel}>
            {label}
          </span>
        )}
      </button>

      {open && (
        <Portal>
          <>
            {video && (
              <div
                className={classNames(
                  'c-tooltip__message c-tooltip-popper__message-video',
                  {
                    'c-tooltip-popper__message--hidden': !open
                  }
                )}
                role="tooltip"
                ref={refs.setFloating}
                style={floatingStyles}
                {...getFloatingProps()}
              >
                {children}
                {videoMessage && (
                  <section className="c-tooltip__info">{videoMessage}</section>
                )}
              </div>
            )}
            {!video && (
              <p
                id={id}
                role="tooltip"
                className={classNames('c-toggletip-message', {
                  'c-toggletip-message--hidden': !open
                })}
                ref={refs.setFloating}
                style={floatingStyles}
                {...getFloatingProps()}
              >
                {children}
              </p>
            )}
          </>
        </Portal>
      )}
    </div>
  );
};

export default ToggletipPopper;
